var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { RelayEnvironmentProvider } from "react-relay";
import { Environment, Network, Observable, RecordSource, Store, } from "relay-runtime";
import { useEnvironment } from "../Shared/components/EnvironmentProvider/EnvironmentProvider";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../Shared/hooks/useLogging/useLogging";
import { getHostFromURL } from "./relayEnvironment-utils";
// Function to create a Relay environment using the provided GraphQL endpoint and host.
const createRelayEnvironment = (graphqlEndpoint, host, dataDogLog) => new Environment({
    network: Network.create(fetchFunction(graphqlEndpoint, host, dataDogLog)),
    store: new Store(new RecordSource(), {
        // The queryCacheExpirationTime option in Relay applies globally to
        // all queries stored in memory via that Relay store instance.
        // https://relay.dev/docs/v16.0.0/guided-tour/reusing-cached-data/staleness-of-data/#query-cache-expiration-time
        queryCacheExpirationTime: 60 * 60 * 1000, // Cache expiration time set to 1 hour
    }),
});
// Fetch function to handle GraphQL requests.
const fetchFunction = (graphqlEndpoint, host, dataDogLog) => (operation, variables) => Observable.create((dataSink) => {
    fetch(graphqlEndpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            RequestHost: host,
        },
        body: JSON.stringify({
            query: operation.text,
            variables,
        }),
    })
        .then((response) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const json = yield response.json();
        if (response.ok) {
            dataSink.next(json);
            dataSink.complete();
        }
        else {
            const errorMessage = ((_b = (_a = json.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.message) || "Unknown error";
            dataDogLog(errorMessage, LogLevel.ERROR, response);
            dataSink.error(new Error(errorMessage));
        }
    }))
        .catch((error) => {
        const errorMessage = error.message || "Network error";
        dataDogLog(errorMessage, LogLevel.ERROR, undefined, error);
        dataSink.error(new Error(errorMessage));
    });
});
// Main RelayEnvironment component
export const RelayEnvironment = ({ children }) => {
    const { datadogLog } = useLogging();
    const { graphQLEndpoint, environment, urlHost, errorMessage } = useEnvironment();
    const graphQLHost = getHostFromURL(environment, urlHost);
    // Use useMemo to ensure the environment is only created once per config
    const relayEnvironment = useMemo(() => {
        if (graphQLEndpoint && graphQLHost && !errorMessage) {
            return createRelayEnvironment(graphQLEndpoint, graphQLHost, datadogLog);
        }
        return undefined;
    }, [graphQLEndpoint, graphQLHost, datadogLog, errorMessage]);
    // Throw an error to display the ErrorBoundary if there is an error or the environment is not created.
    if (errorMessage || !relayEnvironment) {
        const err = errorMessage || "Failed to create Relay Environment";
        throw new Error(err);
    }
    // If there is no error and the environment is created, return the RelayEnvironmentProvider.
    return _jsx(RelayEnvironmentProvider, Object.assign({ environment: relayEnvironment }, { children: children }));
};
